import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelectorAll } from "../../utility/utils";
import { EmptyNodeList } from '../../utility/utils/index';
import { focusableElements as focusableElementsSelector } from '../../utility/utils';
import { throttle } from 'lodash'

interface PopNotificationConstants {
    TIMER: number;
    FOOTER_OFFSET: number;
    TRANSITION: number;
    FLOAT_EL_OFFSET: number;
}

class Texts extends BaseObject { }

class ClassList extends BaseObject {
    show = 'show';
    relatedContent = 'related-content';
    popupFloatAfter = 'pop-after-passed';
    popupFloatAfterPassed = 'js-sticky';
    popupFloatAfterPlaceholder = 'js-intersection-placeholder';
    transitioned = 'js-transitioned';
}

class Selectors extends BaseObject {
    closeBtn = '.pop-notification__close';
    footer = '.footer';
}

class Elements extends BaseElements<Selectors> {
    wrapper: HTMLElement;
    popupFloatAfterPlaceholder: HTMLElement;
    closeBtn: NodeListOf<HTMLElement> = new EmptyNodeList();

    constructor(selectors: Selectors) {
        super(selectors, true);
    }

    setWrapper(wrapper: HTMLElement) {
        this.wrapper = wrapper;
        this.initElements()
    }

    initElements() {
        this.closeBtn = querySelectorAll(this.selectors.closeBtn, this.wrapper);
    }
}

interface PopNotification extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class PopNotification {
    protected _popupFloatAfter: boolean;
    protected _constants: PopNotificationConstants;
    protected _visibility: number;
    protected _focusableElements: NodeListOf<HTMLElement>;
    private _checkVisibilityEvent: (Event) => void;
    private _checkElPassedScrollEvent: (Event) => void;

    constructor(readonly wrapper: HTMLElement) { }

    get constants() {
        return {
            TIMER: 200,
            FOOTER_OFFSET: 200,
            TRANSITION: 250,
            FLOAT_EL_OFFSET: 200
        };
    }

    set visibility(value: string) {
        if (value && value.indexOf('%') > -1) {
            this._visibility = parseFloat(value) / 100.0;
        } else {
            this._visibility = 0;
        }
    }

    initialize(): void {
        this.elements.setWrapper(this.wrapper);
        this._constants = this.constants;
        this._popupFloatAfter = this.wrapper.classList.contains(this.classList.popupFloatAfter);
        this.visibility = this.wrapper.dataset.visibleAfter;
        this._focusableElements = querySelectorAll(focusableElementsSelector, this.wrapper);

        if (this._popupFloatAfter) {
            this.setPopupFloatAfterPlaceholder();
            this.checkElPassedScroll();
        }
        this.checkVisibility();
        this.checkA11y();
        this.addEventListeners();
    }

    addEventListeners(): void {
        this.elements.closeBtn.forEach(item => {
            item.addEventListener('click', this.onClick.bind(this));
        });
        if (this._visibility) {
            this._checkVisibilityEvent = this.checkVisibility.bind(this);
            window.addEventListener('scroll', this._checkVisibilityEvent);
        }

        if (this._popupFloatAfter) {
            this._checkElPassedScrollEvent = this.checkElPassedScroll.bind(this);
            window.addEventListener('scroll', this._checkElPassedScrollEvent);
        }

        if (this._visibility || this._popupFloatAfter) {
            window.addEventListener('scroll', throttle(() => {
                this.checkA11y();
            }, 50));
        }
    }


    setVisibility(visible: boolean): void {
        const { show } = this.classList;
        if (visible) {
            this.wrapper.classList.add(show);
        } else {
            this.wrapper.classList.remove(show);
        }
    }


    checkVisibility(): void {
        const pageHeight = document.body.getBoundingClientRect().height;
        const destinationHeight = pageHeight * this._visibility;
        const scroll = window.pageYOffset;
        const footerRect = document.querySelector(this.selectors.footer)?.getBoundingClientRect();
        const footerDist = footerRect?.height - footerRect?.top + this._constants.FOOTER_OFFSET || -1;
        this.setVisibility(scroll > destinationHeight && footerDist < 0);
    }

    checkElPassedScroll(): void {
        const scroll = window.scrollY;
        const elOffset =
            this.elements.popupFloatAfterPlaceholder.offsetTop +
            this.elements.popupFloatAfterPlaceholder.offsetHeight +
            this._constants.FLOAT_EL_OFFSET;
        this.setFloat(scroll > elOffset);
    }

    checkA11y(): void {
        const { show: showFloated, popupFloatAfterPassed } = this.classList;
        const shouldShow = this._popupFloatAfter
            ? this.wrapper.classList.contains(showFloated) || !this.wrapper.classList.contains(popupFloatAfterPassed)
            : this.wrapper.classList.contains(showFloated);

        this.setA11yState(shouldShow ? 'show' : 'hide');
    }

    setFloat(passed: boolean): void {
        if (passed) {
            this.wrapper.classList.add(this.classList.popupFloatAfterPassed);
            setTimeout(() => this.wrapper.classList.add(this.classList.transitioned), this._constants.TRANSITION);

        } else {
            this.wrapper.classList.remove(this.classList.popupFloatAfterPassed);
            this.wrapper.classList.remove(this.classList.transitioned);
        }
    }

    setA11yState(state: 'hide' | 'show'): void {
        this.wrapper.setAttribute('aria-hidden', state === 'hide' ? 'true' : 'false');
        this._focusableElements?.forEach(el => {
            state === 'hide' ? el.setAttribute('tabindex', '-1') : el.removeAttribute('tabindex');
        })
    }

    onClick(e: Event): void {
        e.preventDefault();
        this.wrapper.classList.remove('show');
        if (this._popupFloatAfter) {
            window.removeEventListener('scroll', this._checkVisibilityEvent);
            window.removeEventListener('scroll', this._checkElPassedScrollEvent);
            this.wrapper.classList.remove(this.classList.popupFloatAfterPassed);
            this.wrapper.classList.remove(this.classList.transitioned);
            return;
        }
        setTimeout(() => {
            this.wrapper.remove();
        }, this._constants.TIMER);
    }

    /**
    * Set a dummy placeholder for the popup to measure the actual position of the element scroll intersection on
    */
    setPopupFloatAfterPlaceholder(): void {
        const popupPlaceholder = document.createElement('div');
        popupPlaceholder.classList.add(this.classList.popupFloatAfterPlaceholder);
        const popupFloatAfterDummyId = `pop-after-passed-placeholder-${Math.floor(Math.random() * 10000)}`;
        popupPlaceholder.setAttribute('id', popupFloatAfterDummyId);
        this.wrapper.insertAdjacentElement('afterend', popupPlaceholder);
        this.elements.popupFloatAfterPlaceholder = document.getElementById(popupFloatAfterDummyId);
    }



}

export default PopNotification;

export {
    PopNotificationConstants,
    Elements as PopNotificationElements,
    Selectors as PopNotificationSelectors,
    ClassList as PopNotificationClassList,
    Texts as PopNotificationTexts
}